import { RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatMoney } from '../../../../lib/formatMoney';
import { PpmpItemModal } from '../components/PpmpItemModal';
import { ppmpItemsApi } from '../procurementCatalogsApi';
import type { PpmpItemCatalogEntry } from '../procurementCatalogTypes';

/**
 * The catalog a procurement line's item description is drawn from.
 *
 * **Its own page rather than a third arm of the two flat vocabularies**, because its row is a
 * different shape: a rich-text specification, a unit and a cost, searched through a derived
 * plain-text column no flat vocabulary has.
 */
export function PpmpItemsIndexPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<PpmpItemCatalogEntry | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    entry: PpmpItemCatalogEntry;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchEntries = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      ppmpItemsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (entry: PpmpItemCatalogEntry): MenuItem[] => [
      {
        key: `edit-${entry.id}`,
        label: 'Edit',
        permission: 'ppmp-items.update',
        onClick: () => setEditing(entry),
      },
      {
        key: `restore-${entry.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'ppmp-items.delete',
        disabledReason: entry.isActive ? 'This catalog item is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', entry }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await ppmpItemsApi.remove(confirming.entry.id);
      else await ppmpItemsApi.restore(confirming.entry.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<PpmpItemCatalogEntry>
        module="ppmp-items"
        breadcrumb={[
          'Procurement',
          'Procurement Planning',
          { label: 'Settings', to: '/procurement/procurement-planning/settings' },
          'PPMP Items',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['PpmpItemCatalog']}
        rowKey={(entry) => entry.id}
        fetch={fetchEntries}
        reloadKey={reloadKey}
        /*
          The placeholder says TEXT, because that is what the search actually matches: `LIKE`
          over stored markup would match every specification CONTAINING a table rather than the
          ones that mention one, so the server reads the derived plain-text column.
        */
        searchPlaceholder="Search item descriptions and units"
        emptyMessage="No catalog items yet. Add the ones this organization procures most often — a line may still describe anything."
        onCreate={() => setCreating(true)}
        createPermission="ppmp-items.create"
        onRowOpen={(entry) => navigate(`/procurement/procurement-planning/settings/ppmp-items/${entry.id}`)}
        openPermission="ppmp-items.read"
        rowActions={rowActions}
        onDelete={(entry) => setConfirming({ kind: 'deactivate', entry })}
        deletePermission="ppmp-items.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(entry) =>
          entry.isActive ? null : 'This catalog item is already deleted.'
        }
        onClearData={() => ppmpItemsApi.clear()}
        clearDataPermission="ppmp-items.clear-data"
        otherInfo={(entry) => entry}
      />

      {creating || editing !== null ? (
        <PpmpItemModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete catalog item' : 'Restore catalog item'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`ppmp-item-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.entry.itemDescriptionText}" stops being offered on a procurement line.`
                  : `"${confirming.entry.itemDescriptionText}" is offered again on a procurement line.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.entry.lineItemCount > 0 ? (
                <p>
                  {confirming.entry.lineItemCount} procurement line
                  {confirming.entry.lineItemCount === 1 ? '' : 's'}
                  {confirming.entry.lineItemCount === 1 ? ' was' : ' were'} drawn from it. Those
                  lines keep the description they snapshotted — retiring a catalog item does not
                  rewrite what a plan says it was buying.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted catalog item can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<PpmpItemCatalogEntry>[] = [
  {
    /*
      THE ANCHOR IS THE PLAIN TEXT, never the markup. A table cell renders one line and the
      specification is routinely a table of dimensions — the whole document is on the profile,
      where it has the room and a card of its own.
    */
    key: 'description',
    header: 'Item',
    width: 420,
    flex: true,
    sortable: true,
    render: (entry) => <TextCell value={entry.itemDescriptionText} />,
  },
  {
    key: 'unit',
    header: 'Unit',
    width: 110,
    sortable: true,
    summary: true,
    render: (entry) => <TextCell value={entry.unit} />,
  },
  {
    key: 'cost',
    header: 'Est. unit cost',
    width: 150,
    align: 'right',
    sortable: true,
    summary: true,
    /*
      AN EM DASH for unpriced, and a muted zero for a cost of zero. Nobody has looked is a
      different fact from free, which is what `formatMoney` renders and what the null keeps
      apart — and `numeric` is what dims the zero.
    */
    render: (entry) => (
      <TextCell
        value={entry.estimatedUnitCost == null ? null : formatMoney(entry.estimatedUnitCost)}
        mono
        numeric
      />
    ),
  },
  {
    key: 'lineItemCount',
    header: 'Procurement lines',
    width: 150,
    align: 'right',
    /*
      NOT a summary value: the list view labels none of them, so a count reads as a bare `· 0`
      at the end of the line. The unit and the cost carry their own meaning; a number does not.
    */
    // Not sortable: a correlated count would have the database compute one per row before it
    // could page, and a header that silently falls back reads as a broken control.
    render: (entry) => <TextCell value={String(entry.lineItemCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (entry) =>
      entry.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
